import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Chip
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import WorkIcon from '@mui/icons-material/Work';
import { API_URL } from '../../config';
import { mockAuthHeaders } from '../../utils/mockAuth';

/**
 * Dialog for saving a bullet to a specific position in the user's profile
 */
const SaveBulletDialog = ({ open, onClose, bullet, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [positions, setPositions] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(null);

  // Load user's profile when dialog opens
  useEffect(() => {
    if (open) {
      loadProfile();
    }
  }, [open]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/api/profile-manager`, {
        headers: {
          ...mockAuthHeaders()
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load profile');
      }

      const data = await response.json();
      const experience = data.experience || [];
      
      setPositions(experience);
      if (experience.length > 0) {
        setSelectedPosition(0); // Select first position by default
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Failed to load your profile. Please try again.');
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (selectedPosition === null) {
      setError('Please select a position');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Get the selected position
      const position = positions[selectedPosition];
      
      // Create new accomplishment from bullet
      const newAccomplishment = {
        text: bullet.text,
        tags: [bullet.type.toLowerCase()] // Add bullet type as tag
      };

      // Add to position's accomplishments
      const updatedPosition = {
        ...position,
        accomplishments: [
          ...(position.accomplishments || []),
          newAccomplishment
        ]
      };

      // Update the specific position using PUT
      const response = await fetch(`${API_URL}/api/profile-manager/experience/${selectedPosition}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...mockAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(updatedPosition)
      });

      if (!response.ok) {
        throw new Error('Failed to save bullet');
      }

      setSaving(false);
      onSaved && onSaved(position);
      onClose();
    } catch (err) {
      console.error('Error saving bullet:', err);
      setError('Failed to save bullet to profile. Please try again.');
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add to Profile</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : positions.length === 0 ? (
          <Alert severity="warning">
            No positions found in your profile. Please add work experience to your profile first.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select which position this accomplishment belongs to:
            </Typography>

            {/* Show the bullet being saved */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Chip label={bullet.type} size="small" color="primary" />
                <Chip 
                  label={`${bullet.scores.actionStrength + bullet.scores.specificity + bullet.scores.impact + bullet.scores.clarity + bullet.scores.achievementFocus}/15`} 
                  size="small" 
                />
              </Box>
              <Typography variant="body2">{bullet.text}</Typography>
            </Box>

            <FormControl component="fieldset">
              <FormLabel component="legend">Positions</FormLabel>
              <RadioGroup
                value={selectedPosition}
                onChange={(e) => setSelectedPosition(parseInt(e.target.value))}
              >
                {positions.map((position, index) => (
                  <FormControlLabel
                    key={index}
                    value={index}
                    control={<Radio />}
                    label={
                      <Box sx={{ py: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <WorkIcon fontSize="small" color="action" />
                          <Typography variant="body1" fontWeight="medium">
                            {position.title}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 4 }}>
                          <BusinessIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {position.company}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            • {position.start_date} - {position.end_date || 'Present'}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                          {(position.accomplishments || []).length} accomplishments
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving || positions.length === 0 || selectedPosition === null}
          startIcon={saving && <CircularProgress size={16} />}
        >
          {saving ? 'Saving...' : 'Save to Profile'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveBulletDialog;
